import React, { useState, useMemo } from 'react';
import { X, RotateCcw } from 'lucide-react';
import { localisation } from '../data/diagnosticData';

/**
 * Schéma dentaire interactif compact
 * Permet de sélectionner: dent(s), quadrant, arcade ou global
 */
export default function DentalChart({
  selectedTeeth = [],
  onSelectionChange,
  compact = false
}) {
  const [hoverTooth, setHoverTooth] = useState(null);

  // Grouper les dents par quadrant pour l'affichage
  const teethByQuadrant = useMemo(() => {
    return {
      Q1: localisation.quadrants[0].dents, // 18-11 (affiché de droite à gauche du patient = gauche à droite pour nous)
      Q2: localisation.quadrants[1].dents, // 21-28
      Q3: localisation.quadrants[2].dents, // 38-31 (affiché de gauche à droite du patient = droite à gauche pour nous)
      Q4: localisation.quadrants[3].dents, // 41-48
    };
  }, []);

  // Vérifier si un quadrant est entièrement sélectionné
  const isQuadrantSelected = (quadrantId) => {
    const quadrant = localisation.quadrants.find(q => q.id === quadrantId);
    return quadrant?.dents.every(d => selectedTeeth.includes(d));
  };

  // Vérifier si une arcade est entièrement sélectionnée
  const isArcadeSelected = (arcadeId) => {
    const arcade = localisation.arcades.find(a => a.id === arcadeId);
    if (!arcade) return false;
    const allTeeth = arcade.quadrants.flatMap(qId =>
      localisation.quadrants.find(q => q.id === qId)?.dents || []
    );
    return allTeeth.every(d => selectedTeeth.includes(d));
  };

  // Toggle une dent
  const toggleTooth = (toothId) => {
    const newSelection = selectedTeeth.includes(toothId)
      ? selectedTeeth.filter(t => t !== toothId)
      : [...selectedTeeth, toothId];
    onSelectionChange(newSelection);
  };

  // Sélectionner un quadrant
  const selectQuadrant = (quadrantId) => {
    const quadrant = localisation.quadrants.find(q => q.id === quadrantId);
    if (!quadrant) return;

    if (isQuadrantSelected(quadrantId)) {
      // Désélectionner le quadrant
      const newSelection = selectedTeeth.filter(t => !quadrant.dents.includes(t));
      onSelectionChange(newSelection);
    } else {
      // Sélectionner le quadrant
      const newSelection = [...new Set([...selectedTeeth, ...quadrant.dents])];
      onSelectionChange(newSelection);
    }
  };

  // Sélectionner une arcade
  const selectArcade = (arcadeId) => {
    const arcade = localisation.arcades.find(a => a.id === arcadeId);
    if (!arcade) return;

    const allTeeth = arcade.quadrants.flatMap(qId =>
      localisation.quadrants.find(q => q.id === qId)?.dents || []
    );

    if (isArcadeSelected(arcadeId)) {
      const newSelection = selectedTeeth.filter(t => !allTeeth.includes(t));
      onSelectionChange(newSelection);
    } else {
      const newSelection = [...new Set([...selectedTeeth, ...allTeeth])];
      onSelectionChange(newSelection);
    }
  };

  // Sélectionner tout
  const selectAll = () => {
    if (selectedTeeth.length === 32) {
      onSelectionChange([]);
    } else {
      onSelectionChange(localisation.dents.map(d => d.id));
    }
  };

  // Effacer la sélection
  const clearSelection = () => {
    onSelectionChange([]);
  };

  // Rendu d'une dent
  const ToothButton = ({ toothId, position = 'top' }) => {
    const isSelected = selectedTeeth.includes(toothId);
    const isHovered = hoverTooth === toothId;

    return (
      <button
        onClick={() => toggleTooth(toothId)}
        onMouseEnter={() => setHoverTooth(toothId)}
        onMouseLeave={() => setHoverTooth(null)}
        className={`
          ${compact ? 'w-6 h-7 text-[10px]' : 'w-8 h-9 text-xs'}
          font-medium rounded-sm transition-all border
          ${isSelected
            ? 'bg-sky-500 text-white border-sky-600 shadow-sm'
            : isHovered
              ? 'bg-sky-100 border-sky-300 text-sky-700'
              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
          }
          ${position === 'top' ? 'rounded-t-lg' : 'rounded-b-lg'}
        `}
        title={localisation.dents.find(d => d.id === toothId)?.nom}
      >
        {toothId}
      </button>
    );
  };

  // Formater la sélection pour l'affichage
  const formatSelection = () => {
    if (selectedTeeth.length === 0) return 'Aucune sélection';
    if (selectedTeeth.length === 32) return 'Bouche entière (Global)';
    if (selectedTeeth.length <= 4) return selectedTeeth.join(', ');

    // Vérifier si c'est un quadrant ou une arcade
    for (const quad of localisation.quadrants) {
      if (quad.dents.every(d => selectedTeeth.includes(d)) &&
          selectedTeeth.every(d => quad.dents.includes(d))) {
        return quad.nom;
      }
    }
    for (const arcade of localisation.arcades) {
      const arcadeTeeth = arcade.quadrants.flatMap(qId =>
        localisation.quadrants.find(q => q.id === qId)?.dents || []
      );
      if (arcadeTeeth.every(d => selectedTeeth.includes(d)) &&
          selectedTeeth.every(d => arcadeTeeth.includes(d))) {
        return arcade.nom;
      }
    }

    return `${selectedTeeth.length} dents sélectionnées`;
  };

  return (
    <div className={`bg-gradient-to-b from-slate-50 to-white rounded-xl border border-slate-200 ${compact ? 'p-3' : 'p-4'}`}>
      {/* Header avec boutons rapides */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`font-semibold text-slate-700 ${compact ? 'text-sm' : 'text-base'}`}>
            Localisation
          </span>
          {selectedTeeth.length > 0 && (
            <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">
              {formatSelection()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={selectAll}
            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
              selectedTeeth.length === 32
                ? 'bg-sky-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Global
          </button>
          {selectedTeeth.length > 0 && (
            <button
              onClick={clearSelection}
              className="p-1 text-slate-400 hover:text-red-500 transition-colors"
              title="Effacer la sélection"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Schéma dentaire */}
      <div className="flex flex-col items-center gap-1">
        {/* Arcade maxillaire */}
        <div className="flex items-center gap-1">
          {/* Bouton Q1 */}
          <button
            onClick={() => selectQuadrant('Q1')}
            className={`${compact ? 'w-5 h-5 text-[8px]' : 'w-6 h-6 text-[10px]'} rounded font-bold transition-colors ${
              isQuadrantSelected('Q1')
                ? 'bg-violet-500 text-white'
                : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
            }`}
            title="Quadrant 1 - Maxillaire droit"
          >
            Q1
          </button>

          {/* Dents Q1 (18-11) affichées de gauche à droite */}
          <div className="flex gap-px">
            {[...teethByQuadrant.Q1].reverse().map(toothId => (
              <ToothButton key={toothId} toothId={toothId} position="top" />
            ))}
          </div>

          {/* Ligne centrale */}
          <div className={`${compact ? 'w-px h-7' : 'w-0.5 h-9'} bg-slate-300`} />

          {/* Dents Q2 (21-28) */}
          <div className="flex gap-px">
            {teethByQuadrant.Q2.map(toothId => (
              <ToothButton key={toothId} toothId={toothId} position="top" />
            ))}
          </div>

          {/* Bouton Q2 */}
          <button
            onClick={() => selectQuadrant('Q2')}
            className={`${compact ? 'w-5 h-5 text-[8px]' : 'w-6 h-6 text-[10px]'} rounded font-bold transition-colors ${
              isQuadrantSelected('Q2')
                ? 'bg-violet-500 text-white'
                : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
            }`}
            title="Quadrant 2 - Maxillaire gauche"
          >
            Q2
          </button>
        </div>

        {/* Boutons arcades */}
        <div className="flex items-center justify-center gap-2 my-1">
          <button
            onClick={() => selectArcade('maxillaire')}
            className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
              isArcadeSelected('maxillaire')
                ? 'bg-emerald-500 text-white'
                : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
            }`}
          >
            Maxillaire
          </button>
          <span className="text-slate-300">|</span>
          <button
            onClick={() => selectArcade('mandibulaire')}
            className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
              isArcadeSelected('mandibulaire')
                ? 'bg-emerald-500 text-white'
                : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
            }`}
          >
            Mandibulaire
          </button>
        </div>

        {/* Arcade mandibulaire */}
        <div className="flex items-center gap-1">
          {/* Bouton Q4 */}
          <button
            onClick={() => selectQuadrant('Q4')}
            className={`${compact ? 'w-5 h-5 text-[8px]' : 'w-6 h-6 text-[10px]'} rounded font-bold transition-colors ${
              isQuadrantSelected('Q4')
                ? 'bg-violet-500 text-white'
                : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
            }`}
            title="Quadrant 4 - Mandibulaire droit"
          >
            Q4
          </button>

          {/* Dents Q4 (48-41) affichées de gauche à droite */}
          <div className="flex gap-px">
            {[...teethByQuadrant.Q4].reverse().map(toothId => (
              <ToothButton key={toothId} toothId={toothId} position="bottom" />
            ))}
          </div>

          {/* Ligne centrale */}
          <div className={`${compact ? 'w-px h-7' : 'w-0.5 h-9'} bg-slate-300`} />

          {/* Dents Q3 (31-38) */}
          <div className="flex gap-px">
            {teethByQuadrant.Q3.map(toothId => (
              <ToothButton key={toothId} toothId={toothId} position="bottom" />
            ))}
          </div>

          {/* Bouton Q3 */}
          <button
            onClick={() => selectQuadrant('Q3')}
            className={`${compact ? 'w-5 h-5 text-[8px]' : 'w-6 h-6 text-[10px]'} rounded font-bold transition-colors ${
              isQuadrantSelected('Q3')
                ? 'bg-violet-500 text-white'
                : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
            }`}
            title="Quadrant 3 - Mandibulaire gauche"
          >
            Q3
          </button>
        </div>
      </div>

      {/* Info sur la sélection */}
      {selectedTeeth.length > 0 && selectedTeeth.length < 32 && selectedTeeth.length > 4 && (
        <div className="mt-3 flex flex-wrap gap-1 justify-center">
          {selectedTeeth.map(toothId => (
            <span
              key={toothId}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded text-[10px]"
            >
              {toothId}
              <button
                onClick={() => toggleTooth(toothId)}
                className="hover:text-sky-900"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
